import { z } from 'zod';

// =============================================================================
// API Response Schemas (Zod at boundaries — Constitution Principle 10)
// =============================================================================

export const LandingPageBrandingSchema = z.object({
  primaryColor: z.string(),
  secondaryColor: z.string(),
  backgroundColor: z.string(),
  logoUrl: z.string().nullable().optional(),
  backgroundImageUrl: z.string().nullable().optional(),
}).passthrough();

export const LandingPageContentSchema = z.object({
  headline: z.string(),
  description: z.string(),
  ctaText: z.string(),
}).passthrough();

export const LandingPageSettingsSchema = z.object({
  showCountdown: z.boolean(),
  countdownEndDate: z.string().nullable().optional(),
  requireEmail: z.boolean(),
}).passthrough();

export const LandingPageGameOptionsSchema = z.object({
  pointsPerCorrectAnswer: z.number().default(1),
  showScoreAfterQuestion: z.boolean().default(true),
  showCorrectAnswer: z.boolean().default(true),
  questionTimeLimit: z.number().nullable().default(null),
});

export const PublicEpisodeInfoSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string(),
  thumbnailUrl: z.string().nullable().optional(),
  duration: z.number().nullable().optional(),
  publishedAt: z.string().nullable().optional(),
  seasonId: z.string().nullable().optional(),
  seasonName: z.string().nullable().optional(),
  seasonNumber: z.number().nullable().optional(),
}).passthrough();

export const PublicShowInfoSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string(),
  thumbnailUrl: z.string().nullable().optional(),
}).passthrough();

export const PublicLandingPageSchema = z.object({
  id: z.string(),
  slug: z.string(),
  title: z.string(),
  targetType: z.enum(['show', 'episode']),
  targetId: z.string(),
  organizationId: z.string(),
  branding: LandingPageBrandingSchema,
  content: LandingPageContentSchema,
  offerContent: z.string().nullable(),
  settings: LandingPageSettingsSchema,
  gameOptions: LandingPageGameOptionsSchema,
  showYakOffer: z.boolean(),
  forwardUrl: z.string().nullable(),
  show: PublicShowInfoSchema.optional(),
  episode: PublicEpisodeInfoSchema.optional(),
  episodes: z.array(PublicEpisodeInfoSchema).optional(),
}).passthrough();

// =============================================================================
// Auth Response Schema
// =============================================================================

/**
 * Auth Response Field Mapping:
 * - API `token` → stored as `accessToken` (yak_access_token)
 * - API `refreshToken` → stored as `refreshToken` (yak_refresh_token)
 * - API `user.id` → stored as `userId` (yak_user_id)
 * - `expiresAt` → derived from JWT `exp` claim or fixed 1h TTL (yak_expires_at)
 * - `redirectUrl` → ignored (play app manages its own funnel)
 *
 * The API returns different shapes based on success/failure:
 * - Success: { success: true, token, refreshToken, user, redirectUrl }
 * - Failure: { success: false, error, errorCode, redirectUrl }
 */
export const AuthResponseSchema = z.discriminatedUnion('success', [
  // Success case
  z.object({
    success: z.literal(true),
    token: z.string(),
    // refreshToken may not be returned if refresh tokens are disabled in FusionAuth
    refreshToken: z.string().optional(),
    user: z.object({
      id: z.string(),
      email: z.string(),
      firstName: z.string().nullable().optional(),
      lastName: z.string().nullable().optional(),
      globalUserId: z.string().optional(),
    }).passthrough(),
    redirectUrl: z.string(),
    wasAlreadyMember: z.boolean().optional(),
    organizationId: z.string().optional(),
  }),
  // Failure case
  z.object({
    success: z.literal(false),
    error: z.string().optional(),
    errorCode: z.string().optional(),
    redirectUrl: z.string(),
  }),
]);

// =============================================================================
// Episode Game Data Schemas
// =============================================================================

export const VideoAssetSchema = z.object({
  url: z.string(),
});

export const QuestionResultSchema = z.object({
  questionId: z.string(),
  selectedAnswerId: z.string(),
  isCorrect: z.boolean(),
  timeToAnswer: z.number(),
});

export const AnswerSchema = z.object({
  id: z.string(),
  text: z.string(),
  isCorrect: z.boolean(),
});

export const QuestionSchema = z.object({
  id: z.string(),
  text: z.string(),
  answers: z.array(AnswerSchema).min(2).max(4),
});

export const CuePointSchema = z.object({
  id: z.string(),
  type: z.enum(['question', 'interstitial', 'ad']),
  timeCode: z.number(),
  name: z.string().optional(),
  duration: z.number().optional(),
  asset: z.object({
    file: z.object({ url: z.string() }),
  }).optional(),
  question: QuestionSchema.optional(),
}).passthrough();

/**
 * Episode Game Data Schema (Single Video Model - FR-009)
 *
 * The play app uses a single combined video that includes intro/outro.
 * The `intro` and `outro` fields are kept optional for backwards compatibility
 * with the API, but only the `video` field is used in the play app.
 */
export const EpisodeGameDataSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string(),
  video: VideoAssetSchema,
  cuePoints: z.array(CuePointSchema),
  // Backwards compatibility - API may still send these but play app ignores them
  intro: VideoAssetSchema.nullable().optional(),
  outro: VideoAssetSchema.nullable().optional(),
}).passthrough();

// =============================================================================
// Analytics Event Schemas
// =============================================================================

export const PublicAnalyticsEventSchema = z.object({
  eventType: z.enum([
    'view',
    'unique_view',
    'registration_start',
    'registration_complete',
    'login_start',
    'login_complete',
    'game_start',
    'game_complete',
    'offer_view',
    'offer_click',
    'offer_dismiss',
  ]),
  sessionId: z.string().optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
});
